import React from "react";
import { Link } from "react-router-dom";
import { Shield, Sun, Moon } from "lucide-react";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { STATUS } from "@/lib/format";

export function Logo({ to = "/", size = "md" }) {
  const box = size === "lg" ? "h-10 w-10" : "h-8 w-8";
  return (
    <Link to={to} className="flex items-center gap-2" data-testid="brand-logo">
      <span className={`${box} rounded-md bg-indigo-600 flex items-center justify-center`}>
        <Shield className="h-4 w-4 text-white" strokeWidth={2.5} />
      </span>
      <span className="font-display font-bold tracking-tight text-lg">ScopeGuard</span>
    </Link>
  );
}

export function ThemeToggle({ className = "" }) {
  const { theme, toggle } = useTheme();
  const { user } = useAuth();
  const onClick = () => {
    const next = theme === "dark" ? "light" : "dark";
    toggle();
    if (user) api.patch("/auth/profile", { theme: next }).catch(() => {});
  };
  return (
    <button data-testid="theme-toggle-btn" onClick={onClick} aria-label="Toggle light and dark mode"
      className={`rounded-full p-2 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors ${className}`}>
      {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

export function StatusBadge({ status, testId }) {
  const s = STATUS[status] || STATUS.pending;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold rounded border ${s.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />{s.label}
    </span>
  );
}

export function Footer({ className = "" }) {
  const { user } = useAuth();
  return (
    <footer className={`border-t border-border mt-16 ${className}`} data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row gap-4 md:items-center md:justify-between text-sm text-muted-foreground">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="font-display font-bold text-foreground">ScopeGuard</span>
          <Link to="/faq" className="hover:text-foreground transition-colors" data-testid="footer-faq-link">FAQ & Help</Link>
          <Link to="/#pricing" className="hover:text-foreground transition-colors" data-testid="footer-pricing-link">Pricing</Link>
          <Link to={user ? "/app" : "/auth"} className="hover:text-foreground transition-colors" data-testid="footer-signin-link">{user ? "Open workspace" : "Sign in"}</Link>
          <Link to="/legal/terms" className="hover:text-foreground transition-colors" data-testid="footer-terms-link">Terms</Link>
          <Link to="/legal/privacy" className="hover:text-foreground transition-colors" data-testid="footer-privacy-link">Privacy</Link>
        </div>
        <div data-testid="footer-credit">© {new Date().getFullYear()} ScopeGuard · Created by <span className="text-foreground font-semibold">Xavier Leon</span></div>
      </div>
    </footer>
  );
}

export function Eyebrow({ children, className = "" }) {
  return <div className={`text-xs uppercase tracking-widest font-semibold text-indigo-600 dark:text-indigo-400 ${className}`}>{children}</div>;
}
